// Copyright (c) Laserfiche.
// Licensed under the MIT License. See LICENSE in the project root for license information.
var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/**
 * Creates a Promise that completes after the specified number of miliseconds
 * @param timeMs - time to sleep in milliseconds
 * @returns
 * @example
 * ```typescript
 * await sleepAsync(3000); // returns in 3 seconds
 * ```
 */
export function sleepAsync(timeMs) {
  const promise = new Promise(resolve => {
    setTimeout(() => resolve(), timeMs);
  });
  return promise;
}
/**
 * Returns a Promise that completes when the test function returns true, or after the timeout has elapsed
 * @param func - a function that returns true when the test condition is met
 * @param timeoutFunc - the function called after a timeout
 * @param timeoutMs - miliseconds to wait for the test function to return true before timing out
 * @returns
 * @example
 * ```typescript
 * let test = 1;
 * const trueFunc = () => {test == 1}
 * const falseFunc = () => {test == 2}
 * const timeoutFunc = () => {console.warn('timeout!')}
 * waitForConditionAsync(trueFunc, timeoutFunc, 3000); // returns right away
 * waitForConditionAsync(falseFunc, timeoutFunc, 3000); // returns in 3s and console.warn('timeout!')
 * ```
 */
export function waitForConditionAsync(func, timeoutFunc, timeoutMs = 2000) {
  return __awaiter(this, void 0, void 0, function* () {
    const startTime = new Date().getTime();
    while (new Date().getTime() - startTime < timeoutMs) {
      if (func()) {
        return;
      } else {
        yield sleepAsync(50);
      }
    }
    timeoutFunc();
  });
}
/**
 * Asynchronous method to force the method to complete asynchronously
 * @example
 * ```typescript
 * this.isLoading = true;
 * await yieldAsync();  // makes sure this.isLoading is true, and the subsequence call is based on this.isLoading being true
 * ```
 */
export function yieldAsync() {
  const promise = new Promise(resolve => {
    setTimeout(() => resolve());
  });
  return promise;
}
/**
 * If value is undefined, it returns the defaultValue, or throws an exception.
 * Else returns the not-undefined value.
 * @param value - value to assert if defined
 * @param paramName - the parameter name used in the error message
 * @param defaultValue - default value when undefined value is passed in
 * @returns defined value
 * @example
 * ```typescript
 * const value1 = undefined;
 * const value2 = 'validString';
 * const defaultString = 'defaultString';
 * const param = 'stringParam';
 *
 * validateDefined(value1, param, defaultString); // 'defaultString'
 * validateDefined(value1, param, undefined); // Error: stringParam undefined
 * validateDefined(value2, param, defaultString); // 'validString'
 * validateDefined(value2, param, undefined); // 'validString'
 * ```
 */
export function validateDefined(value, paramName, defaultValue) {
  if (value === undefined) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`${paramName} undefined`);
  }
  return value;
}
let _isBrowser;
/**
 * Function that determines if the environment is in a browser or not
 * @returns True if the function is run in a browser, false if it is run in another environment
 */
export function isBrowser() {
  if (_isBrowser) {
    return _isBrowser;
  }
  try {
    _isBrowser = window.location !== undefined;
  } catch (_a) {
    _isBrowser = false;
  }
  return _isBrowser;
}
